import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import RunningBoxPreview from "../../../components/RunningBoxPreview";
import DemoPuzzle from "../DemoPuzzle";
import { trackEvent } from "../../../lib/analytics";

const PERSONAS = ["learn", "practice", "compete", "hunt"];

// Hero variant rotates by calendar day and is biased toward the Attack Box
// (box shown ~2 of every 3 days) since that's the surface we most want to
// promote. Changes on alternate days automatically; a hard refresh across a
// day boundary flips it. Both variants still fire A/B analytics events.
function getHeroVariant() {
    try {
        const day = Math.floor(Date.now() / 86400000);
        return day % 3 === 0 ? "puzzle" : "box";
    } catch {
        return "box";
    }
}

function useTypewriter(words, typeMs = 70, holdMs = 1600, eraseMs = 40) {
    const [text, setText] = useState("");
    const [i, setI] = useState(0);
    const [phase, setPhase] = useState("typing");
    useEffect(() => {
        const word = words[i % words.length];
        let t;
        if (phase === "typing") {
            if (text.length < word.length) t = setTimeout(() => setText(word.slice(0, text.length + 1)), typeMs);
            else t = setTimeout(() => setPhase("erasing"), holdMs);
        } else if (text.length > 0) {
            t = setTimeout(() => setText(text.slice(0, -1)), eraseMs);
        } else {
            setI((v) => v + 1); setPhase("typing");
        }
        return () => clearTimeout(t);
    }, [text, phase, i, words, typeMs, holdMs, eraseMs]);
    return text;
}

export default function HeroV3({ puzzle, loading }) {
    const word = useTypewriter(PERSONAS);
    const [variant] = useState(getHeroVariant);
    useEffect(() => {
        trackEvent("hero_variant_view", { variant });
    }, [variant]);
    const ctaTrack = (cta) => trackEvent("hero_cta_click", { variant, cta });
    return (
        <section className="relative border-b border-white/[0.07]" data-testid="hero-v3">
            <div className="absolute inset-0 pointer-events-none opacity-[0.35]"
                 style={{ backgroundImage: "linear-gradient(rgba(148,163,184,0.14) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.14) 1px, transparent 1px)", backgroundSize: "48px 48px", maskImage: "radial-gradient(ellipse 70% 55% at 40% 0%, #000 30%, transparent 100%)" }} />
            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pt-20 sm:pt-28 pb-16 sm:pb-24 grid lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-14 items-center">
                <div className="min-w-0">
                    <div className="font-mono text-[8px] tracking-[0.1em] min-[360px]:text-[9px] min-[360px]:tracking-[0.18em] sm:text-[11px] uppercase sm:tracking-[0.3em] text-cyan-400/80 whitespace-nowrap" data-testid="hero-kicker">
                        // the proving ground for ethical hackers
                    </div>
                    <h1 className="mt-6 font-bold tracking-tighter text-[30px] min-[360px]:text-4xl sm:text-5xl lg:text-6xl text-slate-100 lowercase" style={{ fontFamily: "'Space Grotesk', sans-serif" }} data-testid="hero-headline">
                        hackers<br />
                        <span className="text-cyan-400">{word}</span>
                        <span className="text-cyan-400 animate-pulse">_</span>
                    </h1>
                    <p className="mt-6 max-w-xl text-sm md:text-base leading-relaxed text-slate-400 font-mono lowercase" data-testid="hero-subhead">
                        a serious community for ethical hackers. sharpen your skills in
                        hands-on cybersecurity labs, prove them in live ctf competitions,
                        then hunt real bug bounty programs. verifiable skill — not certificates.
                    </p>
                    <div className="mt-9 flex flex-col min-[360px]:flex-row min-[360px]:items-center gap-2 sm:gap-3">
                        <Link to="/login?mode=register&type=hacker" data-testid="hero-cta-join" onClick={() => ctaTrack("join")}
                              className="w-full min-[360px]:w-auto min-[360px]:flex-1 sm:flex-none bg-cyan-400 text-black text-[10px] sm:text-[12px] uppercase tracking-[0.12em] sm:tracking-[0.2em] font-mono font-bold px-3 sm:px-8 py-3 sm:py-3.5 rounded-full hover:bg-white transition-colors text-center whitespace-nowrap">
                            join the underground →
                        </Link>
                        <Link to="/labs" data-testid="hero-cta-vault" onClick={() => ctaTrack("vault")}
                              className="w-full min-[360px]:w-auto min-[360px]:flex-1 sm:flex-none border border-white/20 text-slate-200 text-[10px] sm:text-[12px] uppercase tracking-[0.12em] sm:tracking-[0.2em] font-mono px-3 sm:px-8 py-3 sm:py-3.5 rounded-full hover:border-cyan-400/60 hover:text-cyan-300 transition-colors text-center whitespace-nowrap">
                            browse the vault
                        </Link>
                    </div>
                    <div className="mt-8 font-mono text-[6.5px] min-[360px]:text-[8px] sm:text-[11px] text-slate-400 lowercase whitespace-nowrap" data-testid="hero-microcopy">
                        // free to start · no card · every flag is verifiable proof of skill
                    </div>
                </div>
                <div className="w-full min-w-0 max-w-md md:max-w-lg lg:max-w-none lg:justify-self-end mx-auto lg:mx-0" data-testid={`hero-variant-${variant}`}>
                    {variant === "puzzle" ? (
                        <DemoPuzzle puzzle={puzzle} loading={loading} />
                    ) : (
                    <div className="relative rounded-xl border border-white/10 bg-[#0a0e17] overflow-hidden shadow-2xl shadow-black/50" data-testid="hero-box-preview">
                        <div className="absolute -inset-px rounded-xl pointer-events-none" style={{ background: "radial-gradient(120% 80% at 80% 0%, rgba(34,211,238,0.10), transparent 60%)" }} />
                        {/* window titlebar */}
                        <div className="relative flex items-center gap-2 px-4 py-3 border-b border-white/[0.07] bg-white/[0.02]">
                            <span className="w-2.5 h-2.5 rounded-full bg-rose-500/70" />
                            <span className="w-2.5 h-2.5 rounded-full bg-amber-500/70" />
                            <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/70" />
                            <span className="ml-2 font-mono text-[10px] text-slate-500 lowercase truncate">attack-box@kali — /root</span>
                            <span className="ml-auto font-mono text-[9px] uppercase tracking-widest text-emerald-400 flex items-center gap-1.5 shrink-0">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> live
                            </span>
                        </div>
                        {/* terminal body */}
                        <div className="relative p-5 min-h-[300px] sm:min-h-[380px]">
                            <RunningBoxPreview />
                        </div>
                        {/* footer CTA */}
                        <div className="relative flex flex-wrap items-center justify-between gap-x-3 gap-y-1 px-5 py-3.5 border-t border-white/[0.07] bg-white/[0.02]">
                            <span className="font-mono text-[10px] text-slate-400 lowercase">// real cloud kali, in your browser</span>
                            <Link to="/box" data-testid="hero-box-cta" onClick={() => ctaTrack("box")} className="font-mono text-[10px] uppercase tracking-widest text-cyan-300 hover:text-cyan-200 transition-colors whitespace-nowrap">
                                try attack box →
                            </Link>
                        </div>
                    </div>
                    )}
                </div>
            </div>
        </section>
    );
}
